import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconCheck, IconLock } from '@/components/Icons';
import { DataSources } from '@/components/DataSources';
import { LanguageSelect } from '@/components/LanguageSelect';
import { KeyPanel } from '@/features/settings/KeyPanel';
import { useT } from '@/i18n';
import { cx } from '@/lib/cx';
import { hasLLM, settingsStore, useSettings } from '@/settings/settingsStore';
import { appStore, useAppState } from '@/state/appStore';
import s from './setup.module.css';

// Step 0 is the welcome: what the site does and where the data lives, before anything is asked of the user.
type Step = 0 | 1 | 2 | 3;

export function SetupView() {
  const { t } = useT();
  const settings = useSettings();
  const app = useAppState();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(0);
  const [dataChoice, setDataChoice] = useState<'finnhub' | 'massive'>(settings.data.massive?.status === 'ok' ? 'massive' : 'finnhub');
  const [portfolioName, setPortfolioName] = useState<string | null>(null);

  const llmOk = hasLLM(settings);
  const dataOk = settings.data.finnhub?.status === 'ok' || settings.data.massive?.status === 'ok';
  const nameValue = portfolioName ?? t('setup.portfolioDefault');

  const finish = async () => {
    if (app.portfolios.length === 0 && nameValue.trim()) await appStore.createPortfolio(nameValue);
    settingsStore.setSetupDone(true);
    navigate('/');
  };

  return (
    <div className="page">
      <header className={s.head}>
        <div className={s.headLeft}>
          <span className={`serif ${s.brand}`}>{t('app.name')}</span>
          <span className="muted" style={{ fontSize: 12 }}>{settings.setupDone ? t('setup.titleAgain') : t('setup.title')}</span>
        </div>
        <div className={s.headRight}>
          <span className={`muted ${s.tagline}`}>{t('setup.tagline')}</span>
          <LanguageSelect className="btn quiet" />
        </div>
      </header>

      <section className={s.wrap}>
        {step === 0 && (
          <>
            <div>
              <h2 className={s.h2}>{t('setup.welcomeTitle')}</h2>
              <p className={`muted ${s.lead}`}>{t('setup.welcomeLead')}</p>
            </div>
            <div className={s.intro}>
              <div className={s.introBlock}>
                <span className="label">{t('setup.introWhatLabel')}</span>
                <ul>
                  <li>{t('setup.introWhat1')}</li>
                  <li>{t('setup.introWhat2')}</li>
                  <li>{t('setup.introWhat3')}</li>
                </ul>
              </div>
              <div className={s.introBlock}>
                <span className="label"><IconLock size={11} /> {t('setup.introPrivacyLabel')}</span>
                <ul>
                  <li>{t('setup.introPrivacy1')}</li>
                  <li>{t('setup.introPrivacy2')}</li>
                  <li>{t('setup.introPrivacy3')}</li>
                  <li>{t('setup.introPrivacy4')}</li>
                </ul>
              </div>
              <div className={s.introBlock}>
                <span className="label">{t('setup.introNextLabel')}</span>
                <ul>
                  <li>{t('setup.introNext1')}</li>
                  <li>{t('setup.introNext2')}</li>
                </ul>
              </div>
            </div>
            <Link to="/about" className={`muted ${s.aboutLink}`}>{t('setup.aboutLink')}</Link>
            <div className={s.nav}>
              {/* Nothing is required: the user can go straight in and configure later from Settings. */}
              <button type="button" className="btn quiet" onClick={() => void finish()}>{t('setup.enterNow')}</button>
              <button type="button" className="btn primary" onClick={() => setStep(1)}>{t('setup.begin')}</button>
            </div>
          </>
        )}

        {step > 0 && <div className={s.steps}>
          <span className={cx(s.step, step > 1 && 'done', step === 1 && 'on')}><span className={s.n}>{step > 1 ? <IconCheck size={12} /> : '1'}</span>{t('setup.step1')}<span className="muted" style={{ fontWeight: 400 }}>{t('setup.optional')}</span>{llmOk && <span className="muted" style={{ fontWeight: 400 }}>{t('setup.verified')}</span>}</span>
          <span className={s.line} />
          <span className={cx(s.step, step > 2 && 'done', step === 2 && 'on')}><span className={s.n}>{step > 2 ? <IconCheck size={12} /> : '2'}</span>{t('setup.step2')}{dataOk && <span className="muted" style={{ fontWeight: 400 }}>{t('setup.verified')}</span>}</span>
          <span className={s.line} />
          <span className={cx(s.step, step === 3 && 'on')}><span className={s.n}>3</span>{t('setup.step3')}</span>
        </div>}

        {step === 1 && (
          <>
            <div>
              <h2 className={s.h2}>{t('setup.llmTitle')}</h2>
              <p className={`muted ${s.lead}`}>{t('setup.llmLead')}</p>
            </div>
            <KeyPanel kind="llm" />
            <div className={s.notice}><IconLock size={18} style={{ color: 'var(--link)', flex: 'none', marginTop: 2 }} /><p style={{ margin: 0 }}>{t('setup.llmNotice')}</p></div>
            <div className={s.nav}>
              <button type="button" className="btn quiet" onClick={() => setStep(0)}>{t('common.back')}</button>
              <button type="button" className="btn primary" onClick={() => setStep(2)}>{llmOk ? t('common.continue') : t('common.skip')}</button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div>
              <h2 className={s.h2}>{t('setup.dataTitle')}</h2>
              <p className={`muted ${s.lead}`}>{t('setup.dataLead')}</p>
            </div>
            <DataSources />
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>{t('setup.dataPick')}</p>
            <div className={s.opts}>
              <button type="button" className={cx(s.opt, dataChoice === 'finnhub' && 'on')} onClick={() => setDataChoice('finnhub')}>
                <div className={s.optHead}><span className={`serif ${s.optTitle}`}>Finnhub</span><span className="badge"><i className="dot" />{t('setup.free')}</span></div>
                <ul><li>{t('setup.finnhub1')}</li><li>{t('setup.finnhub2')}</li><li className="muted">{t('setup.finnhub3')}</li></ul>
                <a href="https://finnhub.io/register" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }} onClick={(e) => e.stopPropagation()}>{t('setup.finnhubApply')}</a>
              </button>
              <button type="button" className={cx(s.opt, dataChoice === 'massive' && 'on')} onClick={() => setDataChoice('massive')}>
                <div className={s.optHead}><span className={`serif ${s.optTitle}`}>Massive Starter</span><span className="badge">{t('setup.massivePrice')}</span></div>
                <ul><li>{t('setup.massive1')}</li><li>{t('setup.massive2')}</li><li className="muted">{t('setup.massive3')}</li></ul>
                <a href="https://massive.com/stocks" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }} onClick={(e) => e.stopPropagation()}>{t('setup.massiveApply')}</a>
              </button>
            </div>
            <KeyPanel kind={dataChoice} />
            <div className={s.notice}><IconLock size={18} style={{ color: 'var(--link)', flex: 'none', marginTop: 2 }} /><p style={{ margin: 0 }}>{t('setup.dataNotice', { host: dataChoice === 'finnhub' ? 'finnhub.io' : 'api.polygon.io (Massive)' })}</p></div>
            <div className={s.nav}>
              <button type="button" className="btn quiet" onClick={() => setStep(1)}>{t('common.back')}</button>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                {!dataOk && <span className="muted" style={{ fontSize: 13 }}>{t('setup.skipHint')}</span>}
                <button type="button" className="btn primary" onClick={() => setStep(3)}>{dataOk ? t('common.continue') : t('common.skip')}</button>
              </div>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div>
              <h2 className={s.h2}>{t('setup.portfolioTitle')}</h2>
              <p className={`muted ${s.lead}`}>{t('setup.portfolioLead')}</p>
            </div>
            {app.portfolios.length === 0 ? (
              <div className={s.field}>
                <span className="label">{t('setup.portfolioName')}</span>
                <input className="input" value={nameValue} onChange={(e) => setPortfolioName(e.target.value)} placeholder={t('setup.portfolioPlaceholder')} />
              </div>
            ) : (
              <p className="muted" style={{ margin: 0 }}>{t('setup.portfolioHave', { n: app.portfolios.length })}</p>
            )}
            <div className={s.nav}>
              <button type="button" className="btn quiet" onClick={() => setStep(2)}>{t('common.back')}</button>
              <button type="button" className="btn primary" onClick={() => void finish()}>{t('setup.enter')}</button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
